import { Heading, VStack, Button, Box, Table, Thead, Tbody, Tr, Th, Td, useToast, useColorModeValue, HStack, Text, Badge } from '@chakra-ui/react';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Tooltip } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FiEye, FiSlash } from "react-icons/fi";
import { parseUtc, fmtLocal } from "../../utils/dates"; // ← adjust path to your actual utils/dates.ts location

interface Coupon {
  coupon_id: number;
  coupon_code: string;
  description?: string;
  discount_type: string;
  discount_value: number;
  start_date?: string;
  end_date?: string;
  max_redemptions?: number;
  redemption_count: number;
  is_active: boolean;
  applicable_offer_id?: number;
  batch_label?: string;
  created_at: string;
}

const B2CCouponManager = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const navigate = useNavigate();
  const toast = useToast();
  const tableHeaderbg = useColorModeValue("gray.200", "gray.700");

  const fetchCoupons = useCallback(async () => {
    try {
      const apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
      const response = await axios.get(`${apiurl}/coupons`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('b2c_token')}` },
      });
      setCoupons(response.data);
    } catch (error) {
      console.error("Failed to load Coupons", error);
    }
  }, []);

  useEffect(() => { fetchCoupons(); }, [fetchCoupons]);

  const handleDeactivate = async (coupon_id: number) => {
    try {
      const apiurl = process.env.REACT_APP_B2C_API_URL || 'https://default-url.com';
      await axios.put(`${apiurl}/coupon/${coupon_id}/deactivate`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('b2c_token')}` },
      });
      await fetchCoupons();
      toast({ title: "Coupon deactivated", status: "success" });
    } catch (err: any) {
      toast({ title: err?.response?.data?.detail || "Error deactivating coupon", status: "error" });
    }
  };

  const handleAddNewCoupon = () => {
    navigate(`/practice/app/b2c-coupon-manager/0/review-coupon/`);
  };

  const handleReviewCoupon = (coupon_id: number) => {
    navigate(`/practice/app/b2c-coupon-manager/${coupon_id}/review-coupon`);
  };

  // FIX: new Date(coupon.end_date) treated the backend's naive-UTC string
  // as local time, which could make an already-expired coupon (in UTC)
  // read as "not yet expired" or vice-versa depending on the browser's
  // timezone offset. parseUtc anchors it correctly before comparing.
  const isExpired = (coupon: Coupon) => {
    if (!coupon.end_date) return false;
    return parseUtc(coupon.end_date) < new Date();
  };

  const isExhausted = (coupon: Coupon) => {
    if (coupon.max_redemptions === null || coupon.max_redemptions === undefined) return false;
    return coupon.redemption_count >= coupon.max_redemptions;
  };

  return (
    <Box as="main" mx="auto" p={10}>
      <VStack>
        <Heading size="md">B2C Coupon Manager</Heading>
      </VStack>

      <Box mt={4}>
        <Text fontWeight="bold">Existing Coupons</Text>
        <HStack mt={2}>
          <Button borderRadius="sm" fontSize="sm" fontWeight="normal" onClick={handleAddNewCoupon}>
            + Coupon
          </Button>
        </HStack>
      </Box>

      <Box mt={4}>
        <Table mt={4} borderColor="gray.300" borderWidth={1} borderRadius="sm">
          <Thead fontSize="12">
            <Tr bgColor={tableHeaderbg}>
              <Th>Code</Th>
              <Th>Batch / Description</Th>
              <Th>Discount</Th>
              <Th>Window</Th>
              <Th>Redemptions</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody fontSize="12">
            {coupons.map((coupon) => (
              <Tr key={coupon.coupon_id}>
                <Td fontWeight="semibold">{coupon.coupon_code}</Td>
                <Td>{coupon.batch_label || coupon.description || "-"}</Td>
                <Td>
                  {coupon.discount_type === "PERCENTAGE"
                    ? `${coupon.discount_value}%`
                    : `$${coupon.discount_value}`}
                </Td>
                <Td>
                  {/* fmtLocal parses the stored UTC value correctly before
                      rendering, matching the fix applied on the review page */}
                  {coupon.start_date ? fmtLocal(coupon.start_date, { hour: undefined, minute: undefined }) : "Anytime"}
                  {" – "}
                  {coupon.end_date ? fmtLocal(coupon.end_date, { hour: undefined, minute: undefined }) : "No expiry"}
                </Td>
                <Td>
                  {coupon.redemption_count}
                  {coupon.max_redemptions !== null && coupon.max_redemptions !== undefined
                    ? ` / ${coupon.max_redemptions}`
                    : " / ∞"}
                </Td>
                <Td>
                  {!coupon.is_active ? (
                    <Badge colorScheme="gray">Deactivated</Badge>
                  ) : isExpired(coupon) ? (
                    <Badge colorScheme="orange">Expired</Badge>
                  ) : isExhausted(coupon) ? (
                    <Badge colorScheme="red">Exhausted</Badge>
                  ) : (
                    <Badge colorScheme="green">Active</Badge>
                  )}
                </Td>
                <Td>
                  <Tooltip label="View details & redemptions">
                    <Button size="sm" onClick={() => handleReviewCoupon(coupon.coupon_id)} mr={2}>
                      <FiEye />
                    </Button>
                  </Tooltip>
                  {coupon.is_active && (
                    <Tooltip label="Deactivate">
                      <Button
                        size="sm"
                        colorScheme="red"
                        onClick={() => {
                          if (window.confirm("Deactivate this coupon? This cannot be undone from here.")) {
                            handleDeactivate(coupon.coupon_id);
                          }
                        }}
                      >
                        <FiSlash />
                      </Button>
                    </Tooltip>
                  )}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default B2CCouponManager;